import { useNavigate } from 'react-router-dom'
import { FiChevronLeft } from 'react-icons/fi'
import { faFull, faPlain, longJalali } from 'utilities/helper/format'
import { TMemberFilterSource } from 'modules/fund/tabs/memberFilter'
import { IRecordCopy, recordCopy } from '../copy'

/** Shared card shell — same anatomy as every admin-dashboard section. */
export function Card({
    title,
    aside,
    children,
}: {
    title: string
    aside?: React.ReactNode
    children: React.ReactNode
}) {
    return (
        <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
            <div className=" flex flex-wrap items-baseline justify-between gap-2">
                <p className=" text-sm font-bold text-text-body">{title}</p>
                {aside}
            </div>
            {children}
        </section>
    )
}

function Tile({
    label,
    value,
    note,
    tone = 'plain',
}: {
    label: string
    value: string
    note?: string
    tone?: 'plain' | 'danger' | 'success'
}) {
    const tones = {
        plain: ' bg-canvas text-text-body',
        danger: ' bg-danger-soft text-danger-on',
        success: ' bg-success-2 text-success-on',
    }
    return (
        <div className={` rounded-[10px] p-2.5 ${tones[tone]}`}>
            <p className=" text-[11px] opacity-80">{label}</p>
            <p className=" mt-1 text-sm font-bold tabular-nums">{value}</p>
            {!!note && <p className=" mt-0.5 text-[10px] opacity-80">{note}</p>}
        </div>
    )
}

/** سرمایه در صندوق — its own card, ahead of what is owed. */
export function CapitalCard({
    record,
    title = recordCopy('about').capitalTitle,
}: {
    record: IAdminMemberRecord
    title?: string
}) {
    return (
        <Card title={title}>
            <p className=" mt-2 text-2xl font-bold tabular-nums text-[#0A93A6]">
                {faFull.format(record.capital)}{' '}
                <span className=" text-sm font-normal text-text-secondary">
                    تومان
                </span>
            </p>
            <div className=" mt-3 grid grid-cols-2 gap-2">
                <Tile label="مجموع واریزها" value={faFull.format(record.deposited)} />
                <Tile label="مجموع برداشت‌ها" value={faFull.format(record.withdrawn)} />
            </div>
        </Card>
    )
}

/**
 * وام‌ها و بازپرداخت — the loans block, tiles and all.
 *
 * This used to be two cards: a «وام‌های جاری» headline card and a repayment
 * card with the loan list under it. The headline card said almost nothing the
 * rows below did not already say, so it went — but two of its numbers had
 * nowhere else to live and are here now:
 *
 *   مانده کل — the per-loan rows each show their own remaining; nothing added
 *              them up, and the total is the number a manager says out loud.
 *   معوق     — «میانگین تأخیر» says how late, never how much.
 *
 * «سررسیدنشده» did NOT come across: it is exactly مانده کل − معوق, and a tile
 * whose value the reader can derive from the two beside it costs attention
 * and returns nothing.
 */
export function LoansSection({
    record,
    children,
    copy = recordCopy('about'),
}: {
    record: IAdminMemberRecord
    children?: React.ReactNode
    copy?: IRecordCopy
}) {
    if (!record.loans.count) return null

    const overdue = record.repayment.overdue_amount
    const net = record.loans.outstanding - record.capital

    return (
        <Card
            title="وام‌ها و بازپرداخت"
            aside={
                <span className=" text-[11px] text-text-secondary">
                    {faPlain.format(record.loans.count)} وام، مجموع{' '}
                    {faFull.format(record.loans.total_amount)} تومان
                </span>
            }
        >
            <div className=" mt-3 grid grid-cols-2 gap-2">
                <Tile
                    label="مانده کل"
                    value={faFull.format(record.loans.outstanding)}
                    note="تومان"
                />
                <Tile
                    label="معوق"
                    tone={overdue > 0 ? 'danger' : 'success'}
                    value={faFull.format(overdue)}
                    note={
                        overdue > 0
                            ? `${faPlain.format(
                                  record.repayment.overdue_count
                              )} قسط، تا ${faPlain.format(
                                  record.repayment.max_days_overdue
                              )} روز`
                            : 'قسط معوقی ندارد'
                    }
                />
                <Tile
                    label="نظم بازپرداخت"
                    value={
                        record.repayment.on_time_rate === null
                            ? 'سابقه‌ای ندارد'
                            : `${faPlain.format(
                                  Math.round(record.repayment.on_time_rate)
                              )}٪ به‌موقع`
                    }
                />
                <Tile
                    label="میانگین تأخیر"
                    value={
                        record.repayment.mean_days_late > 0
                            ? `${faPlain.format(
                                  record.repayment.mean_days_late
                              )} روز`
                            : 'بدون تأخیر'
                    }
                />
            </div>

            {record.loans.outstanding > 0 && (
                <p className=" mt-3 border-t border-divider pt-3 text-xs leading-6 text-text-body">
                    {net > 0
                        ? copy.netOwing(faFull.format(net))
                        : copy.netLending(faFull.format(Math.abs(net)))}
                </p>
            )}

            {children}
        </Card>
    )
}


const GUARANTEE_DOT: Record<string, string> = {
    ok: '#74B743',
    late: '#FFB114',
    overdue: '#F24C3D',
}

export function GuaranteesCard({
    data,
    moneypoolId,
    copy = recordCopy('about'),
    canOpenOthers = true,
}: {
    data: IMemberGuarantees
    moneypoolId?: string
    copy?: IRecordCopy
    /**
     * Whether tapping a name may open THAT person's کارنامه. False for a
     * member reading their own record (pwa#61): the backend allows a
     * member their own record and nobody else's, so the tap would only
     * produce a 403 screen. The names still render — who you vouched for,
     * and whether they are paying, is your own exposure and the reason
     * this card is worth reading.
     */
    canOpenOthers?: boolean
}) {
    const navigate = useNavigate()
    const hasAny =
        data.loan_guarantees.length > 0 || data.membership_guarantees.length > 0
    if (!hasAny) return null

    // Everyone named on this card is a member of the same fund, so for a
    // manager every name here is a کارنامهٔ عضو they may want next — «who
    // did this person vouch for» is usually asked on the way to «and how
    // are THEY doing».
    const canOpen = canOpenOthers && !!moneypoolId
    const openRecord = (poolshipId: number | null) => {
        if (!poolshipId || !canOpen) return
        navigate(`/fund/member-record/${moneypoolId}/${poolshipId}`)
    }

    return (
        <Card title={copy.guaranteesTitle}>
            {data.loan_guarantees.length > 0 && (
                <div className=" mt-3 grid grid-cols-2 gap-2">
                    <Tile label="تعهد فعلی" value={faFull.format(data.exposure)} />
                    <Tile
                        label="معوقات ضمانت‌شده‌ها"
                        tone={data.overdue_amount > 0 ? 'danger' : 'success'}
                        value={faFull.format(data.overdue_amount)}
                    />
                </div>
            )}

            {data.loan_guarantees.length > 0 && (
                <>
                    <p className=" mt-4 text-xs font-bold text-heading">ضمانت وام</p>
                    <div className=" mt-2 flex flex-col gap-2">
                        {data.loan_guarantees.map((row) => (
                            <div
                                key={row.loan_id}
                                role={
                                    row.borrower_poolship_id && canOpen
                                        ? 'button'
                                        : undefined
                                }
                                tabIndex={
                                    row.borrower_poolship_id && canOpen
                                        ? 0
                                        : undefined
                                }
                                onClick={() =>
                                    openRecord(row.borrower_poolship_id)
                                }
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault()
                                        openRecord(row.borrower_poolship_id)
                                    }
                                }}
                                className={` flex items-center gap-2.5 rounded-[10px] border border-divider p-2.5 ${
                                    row.borrower_poolship_id && canOpen
                                        ? ' cursor-pointer hover:bg-canvas'
                                        : ''
                                }`}
                            >
                                <div className=" min-w-0 flex-1">
                                    <p className=" break-words text-xs font-bold text-text-body">
                                        {row.borrower_name || 'بدون نام'}
                                    </p>
                                    <p className=" mt-0.5 text-[10px] text-text-secondary">
                                        وام {faFull.format(row.loan_amount)}، مانده{' '}
                                        {faFull.format(row.remaining)}
                                    </p>
                                </div>
                                <span
                                    className={` shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                        row.overdue_amount > 0
                                            ? ' bg-danger-soft text-danger-on'
                                            : ' bg-success-2 text-success-on'
                                    }`}
                                >
                                    {row.overdue_amount > 0
                                        ? `${faPlain.format(
                                              row.max_days_overdue
                                          )} روز معوق`
                                        : 'منظم'}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {data.membership_guarantees.length > 0 && (
                <>
                    <p className=" mt-4 text-xs font-bold text-heading">
                        ضمانت عضویت
                    </p>
                    <p className=" mt-1 text-[11px] leading-5 text-text-secondary">
                        {copy.guaranteesNote(
                            faPlain.format(data.membership_guarantees.length)
                        )}
                    </p>
                    <div className=" mt-2 flex flex-wrap gap-1.5">
                        {data.membership_guarantees.map((row) => (
                            <button
                                key={row.poolship_id}
                                type="button"
                                disabled={!canOpen}
                                onClick={() => openRecord(row.poolship_id)}
                                className=" flex min-h-[32px] items-center gap-1.5 rounded-full border border-divider px-2.5 py-1 text-[11px] text-text-body enabled:hover:border-brand enabled:hover:bg-brand-soft"
                            >
                                <span
                                    className=" h-[7px] w-[7px] rounded-full"
                                    style={{ background: GUARANTEE_DOT[row.status] }}
                                />
                                {row.member_name}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </Card>
    )
}

const KIND_LABEL: Record<string, string> = {
    cnp: 'حق عضویت',
    cnpr: 'حق عضویت',
    cnr: 'قسط وام',
    cnc: 'افزایش موجودی',
    cnhe: 'هبه',
    cnhc: 'هبه',
    ctl: 'وام دریافتی',
    ctc: 'برداشت',
}

/**
 * آخرین تراکنش‌ها — the member's newest rows (the server sends fifteen).
 *
 * The card is a preview, and the whole list is the fund's own تراکنش‌ها tab
 * narrowed to this member (`?poolship=` on the server). Both the link under
 * the rows and a tap on any row go there: a row here carries no id to open on
 * its own, and on that list the same row is at the top, where tapping it opens
 * its details. Without `onOpenAll` the card stays the read-only preview.
 */
export function TransactionsCard({
    rows,
    onOpenAll,
    allLabel = recordCopy('about').allTransactions,
}: {
    rows: IMemberTransaction[]
    onOpenAll?: (source: TMemberFilterSource) => void
    allLabel?: string
}) {
    if (!rows.length) return null
    const openFromRow = onOpenAll
        ? () => onOpenAll('member_record_row')
        : undefined
    return (
        <Card title="آخرین تراکنش‌ها">
            <div className=" mt-1 flex flex-col">
                {rows.map((row, index) => (
                    <div
                        key={index}
                        role={openFromRow ? 'button' : undefined}
                        tabIndex={openFromRow ? 0 : undefined}
                        onClick={openFromRow}
                        onKeyDown={(event) => {
                            if (!openFromRow) return
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                openFromRow()
                            }
                        }}
                        className={` flex items-center gap-2.5 border-b border-divider py-2.5 last:border-b-0${
                            openFromRow ? ' cursor-pointer hover:bg-canvas' : ''
                        }`}
                    >
                        <div className=" min-w-0 flex-1">
                            <p className=" text-xs font-bold text-text-body">
                                {KIND_LABEL[row.kind] || 'تراکنش'}
                                {row.is_cancelled && (
                                    <span className=" mr-1.5 rounded-full bg-danger-soft px-1.5 py-0.5 text-[10px] text-danger-on">
                                        لغو شده
                                    </span>
                                )}
                            </p>
                            <p className=" mt-0.5 text-[10px] text-text-secondary">
                                {longJalali(row.date)}
                            </p>
                        </div>
                        {/* Outflows are NOT danger-red. On a page about one
                            member, red next to «وام دریافتی» reads as money
                            they owe rather than money they were given; red is
                            reserved here for actual problems (معوق, لغو شده).
                            The «−» and the label carry the direction. */}
                        <span
                            className={` shrink-0 whitespace-nowrap text-xs font-bold tabular-nums ${
                                row.direction === 'in'
                                    ? ' text-success-on'
                                    : ' text-text-body'
                            }`}
                        >
                            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                                {row.direction === 'out' ? '−' : ''}
                                {faFull.format(row.amount)}
                            </span>{' '}
                            <span className=" font-normal text-text-secondary">
                                تومان
                            </span>
                        </span>
                    </div>
                ))}
            </div>
            {onOpenAll && (
                <button
                    type="button"
                    onClick={() => onOpenAll('member_record_link')}
                    className=" mt-2 flex min-h-[44px] w-full items-center justify-center gap-1 rounded-[10px] bg-canvas px-3 text-xs font-bold text-brand-deep"
                >
                    {allLabel}
                    <FiChevronLeft aria-hidden className=" h-4 w-4" />
                </button>
            )}
        </Card>
    )
}
